import { RemoteInterviewReport } from "@hireroo/app-store/widget/e/RemoteInterviewReport";
import * as TimeFormatter from "@hireroo/formatter/time";
import * as Graphql from "@hireroo/graphql/client/urql";
import { useLanguageCode, useTranslation } from "@hireroo/i18n";
import { resolveLanguage } from "@hireroo/i18n/utils";
import { Widget } from "@hireroo/presentation";
import { generateHelpCenterUrl } from "@hireroo/router/api";
import * as React from "react";

export type GenerateRemoteInterviewDetailPropsArgs = {};

type AiEvaluationSkillField = Extract<
  keyof Graphql.LiveCodingAiEvaluation,
  "codingSkillEvaluation" | "communicationSkillEvaluation" | "problemSolvingSkillEvaluation" | "systemDesignSkillEvaluation"
>;

export const useGenerateProps = (_args: GenerateRemoteInterviewDetailPropsArgs): Widget.AiEvaluationProps => {
  const { t } = useTranslation();
  const aiEvaluation = RemoteInterviewReport.useAiEvaluation();
  const lang = useLanguageCode();

  const skillsConfig = React.useMemo(
    (): { key: AiEvaluationSkillField; name: string }[] =>
      [
        {
          key: "codingSkillEvaluation",
          name: t("コーディングスキル"),
        },
        {
          key: "systemDesignSkillEvaluation",
          name: t("設計力"),
        },
        {
          key: "problemSolvingSkillEvaluation",
          name: t("課題解決能力"),
        },
        {
          key: "communicationSkillEvaluation",
          name: t("コミュニケーション能力"),
        },
      ] as const,
    [t],
  );

  const skills = React.useMemo((): Widget.AiEvaluationProps["skills"] => {
    return skillsConfig
      .filter(({ key }) => aiEvaluation?.[key])
      .map(({ key, name }) => ({
        name,
        stars: aiEvaluation?.[key]?.numStars ?? 0,
        comment: resolveLanguage(
          {
            qualitativeCommentJa: aiEvaluation?.[key]?.qualitativeCommentJa ?? "",
            qualitativeCommentEn: aiEvaluation?.[key]?.qualitativeCommentEn ?? "",
          },
          lang,
          "qualitativeComment",
        ),
      }));
  }, [aiEvaluation, lang, skillsConfig]);

  const averageStar = React.useMemo((): number => {
    const validSkills = skills.filter(skill => skill.stars > 0);
    const totalStars = validSkills.reduce((acc, skill) => acc + skill.stars, 0);
    return validSkills.length > 0 ? totalStars / validSkills.length : 0;
  }, [skills]);

  const fallbackMessage = React.useMemo((): Widget.AiEvaluationProps["fallbackMessage"] => {
    if (!aiEvaluation) {
      return t("アプリケーション内でビデオ通話を行っていないため、AI評価を表示できません。");
    }
    if (aiEvaluation.status === "ENQUEUED") {
      return t("AI評価を生成中です。しばらくお待ちください。");
    }
    if (aiEvaluation.status === "FAILED") {
      return t("AI評価の生成に失敗しました。運営までお問い合わせください。");
    }
    return undefined;
  }, [aiEvaluation, t]);

  return {
    evaluatedAt: TimeFormatter.unixToDateFormat(aiEvaluation?.evaluatedAtSeconds ?? 0),
    skills,
    totalEvaluation: resolveLanguage(
      { overallAssessmentJa: aiEvaluation?.overallAssessmentJa ?? "", overallAssessmentEn: aiEvaluation?.overallAssessmentEn ?? "" },
      lang,
      "overallAssessment",
    ),
    averageStar,
    fallbackMessage,
    helpLink: !aiEvaluation
      ? {
          text: t("AI評価を表示するには？"),
          href: generateHelpCenterUrl(lang, "REMOTE_AI_EVALUATION"),
        }
      : undefined,
  };
};
